<template>
  <!--
  - Display an error if there's an error loading objects
   -->
  <mp-alert-error v-if="errors.length > 0" :errors="errors">
    There was one or more errors loading Recordings
  </mp-alert-error>

  <div v-else>
    <!--
    - Display a sliding panel allow the editing of objects
     -->
    <mp-sliding-panel
      title="Edit Recording"
      icon="text-single"
      subtitle="Edit the details for this recording"
      :expanded="edit.show"
      @close="closeEdit"
    >
      <!--
      - Display a form allowing the editing of objects
       -->
      <edit-recording-form
        :id="edit.id"
        @input="editUpdated"
        @close="closeEdit"
      />
    </mp-sliding-panel>

    <!--
    - Otherwise display a table of objects
     -->
    <mp-data-table
      :sort="table.sort"
      :total-results="table.totalResults"
      :headers="headers"
      :offset="table.offset"
      :filters="table.filters"
      :loading="loading"
      @state-changed="onStateChange"
    >
      <!--
      - Loop through all objects returned
      -->
      <tr v-for="recording in recordings" :key="recording.id()">
        <td>{{ recording.get("jobName") }}</td>
        <td>{{ recording.get("stationName") }}</td>
        <td>{{ recording.getDate("startTime") | date }}</td>
        <td>{{ recording.get("duration") }}s</td>
        <td class="status">
          <mp-status-bullet
            :type="recording.getBool('isRecording') ? 'success' : 'danger'"
          />
        </td>

        <!--
        - Display a collections of actions for this object
        -->
        <td class="action">
          <mp-action-dropdown>
            <mp-action-dropdown-item icon="edit" @click="editObject(recording)">
              Edit Recording
            </mp-action-dropdown-item>
            <mp-action-dropdown-item
              icon="edit"
              @click="viewRecording(recording)"
            >
              View Recording
            </mp-action-dropdown-item>
            <mp-action-dropdown-item
              icon="delete"
              @click="removeObject(recording)"
            >
              Remove Recording
            </mp-action-dropdown-item>
          </mp-action-dropdown>
        </td>
      </tr>
    </mp-data-table>
  </div>
</template>

<script>
import { API } from "@memberpoint/mp-frontend-core";
import { RecordingManager } from "../Manager/RecordingManager";
import EditRecordingForm from "./internal/EditRecordingForm";
import VideoPlayer from "./internal/VideoPlayer";
import fnsFormat from "date-fns/format";
import Filters from "../Lib/Filters";
import DataTableFilters from "@/VueResources/Mixins/DataTableFilters";

export default {
  name: "RecordingsRecordingList",
  components: { EditRecordingForm },
  filters: {
    date(date) {
      return fnsFormat(date, "Pp");
    }
  },
  mixins: [DataTableFilters],
  data() {
    return {
      errors: [],
      loading: true,
      recordings: [],
      table: {
        /**
         *@type {APIUrlBuilder|null}
         */
        builder: null,
        totalResults: 0,
        filters: Filters,
        offset: 0,
        sort: { start_datetime: "desc" }
      },
      headers: [
        { name: "job", title: "Job", sortable: true },
        { name: "station", title: "Station", sortable: true },
        {
          name: "start_datetime",
          title: "Start Time",
          sortable: true
        },
        { name: "duration", title: "Duration" },
        { name: "isRecording", title: "isRecording" },
        { name: "", title: "" }
      ],
      edit: {
        show: false,
        id: null
      }
    };
  },
  created() {
    this.table.filters = this.parseFiltersFromUrl(
      this.table.filters
    ).getFilters();
  },
  methods: {
    /**
     * Load up a list of all recordings
     */
    load() {
      this.loading = true;
      let manager = new RecordingManager();
      manager
        .getRecordings(this.table.builder)
        .then(apiResponse => {
          this.recordings = apiResponse.getData();
          this.table.totalResults = apiResponse.getTotalResults();
        })
        .catch(err => {
          this.errors = API.SimpleAPIErrorParser.parse(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onStateChange(event) {
      if (event.getStateChangeType() !== "row-selection") {
        // update the url route with filters.
        this.DataTableFiltersRouterUpdate(event.getFilters());

        // update the url builder instance
        this.table.builder = this.buildUrlBuilderFromDataTableState(event);
        this.table.offset = event.getPaginationOffset();

        if (event.getStateChangeType() === "filters") {
          this.table.offset = 0;
          this.table.builder.setPagination(event.getPaginationLimit(), 0);
        }

        // re-load members.
        this.load();
      }
    },

    /**
     *
     * @param {SimpleAPIData} object
     */
    editObject(object) {
      this.edit = {
        show: true,
        id: object.id()
      };
    },
    viewRecording(object) {
      this.$dialog.vueComponent(
        VideoPlayer,
        {
          recordingId: object.id()
        },
        "",
        { allowClose: true }
      );
    },

    /**
     * Fired when an edit form has been completed.
     */
    editUpdated() {
      this.load();
      this.closeEdit();
    },

    /**
     * Close the edit panel
     */
    closeEdit() {
      this.edit = {
        show: false,
        id: null
      };
    },

    /**
     * @param {SimpleAPIData} object
     * Remove Recording
     */
    removeObject(object) {
      let manager = new RecordingManager();
      this.$dialog
        .confirm(
          "Remove Recording",
          `Are you sure you want to remove the recording ${object.id()}?`,
          {
            onConfirm() {
              return manager.removeRecording(object.id());
            }
          }
        )
        .then(() => {
          this.load();
        })
        .catch(() => {
          // Do nothing
        });
    }
  }
};
</script>

<style scoped lang="scss">
.action {
  max-width: 50px;
  text-align: right;
}
.status {
  width: 60px;
  text-align: center;
}
</style>
